import { useDispatch, useSelector } from "react-redux";
import "../../../../styles/screens/client-visit-details/manage-module/SpeakersList.css";
import noTitanOfIndustry from "../../../../assets/no-titan-of-industry.svg";
import Loader from "../../../common-components/Loader";
import { useEffect, useState, useRef } from "react";
import {
  getAllAttendeesOfVisit,
  getAllSpeakers,
  getAllAttendes,
  getAllSpeakersOfVisit,
  resetErrorAlert,
} from "../../../../slices/SpeakerSlice";
import { TitansOfIndustryStringLabels } from "../../../../util/Strings";
import ErrorAlert from "../../../common-components/ErrorAlert";
import ErrorMessages from "../../../../util/ErrorMessages";
import BlackButton from "../../../custom-components/BlackButton";
import VisitSpeakerCard from "../VisitSpeakerCard";
import SpeakerCard from "../SpeakerCard";
import {
  updateVisitSpeakers,
  updateVisitAttendees,
} from "../../../../slices/VisitSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import DraggableList from "../../../common-components/DraggableList";
import CloseButton from "../../../custom-components/CloseButton";

import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

function VisitSpeakersList({ isSpeakers }) {
  const dispatch = useDispatch();

  const data = useSelector((state) => state.speakers.speakerData);
  const attendeesData = useSelector((state) => state.speakers.attendeeData);
  const modalData = isSpeakers ? data : attendeesData;

  const visitSpeakerData = useSelector((state) => state.speakers.visitSpeakerData);
  const visitAttendeeData = useSelector((state) => state.speakers.visitAttendeeData);
  const updatedVisitSpeakerData = useSelector((state) => state.visits.updatedVisitData);

  const isLoading = useSelector((state) => state.speakers.isLoading);
  const id = useSelector((state) => state.visits.selectedVisitId);

  const selectedOfficeId = useSelector((state) => state.officelist.selectedOfficeId);
  const errorMessageCode = useSelector((state) => state.speakers.errorCode);

  const [showModal, setShowModal] = useState(false);
  const [showSorting, setSorting] = useState(false);

 
  const [preSelectedIds, setPreSelectedIds] = useState([]);

  const [modalInput, setModalInput] = useState("");
  const [debouncedModalInput, setDebouncedModalInput] = useState("");
  const [updatedSequence, setUpdatedSequence] = useState([]);

  // Pagination state for modal
  const [modalPage, setModalPage] = useState(1);
  const modalLimit = 50;

  // Total counts for pagination
  const totalSpeakers = useSelector((state) => state.speakers.totalSpeakers);
  const totalAttendees = useSelector((state) => state.speakers.totalAttendees);

  const totalModalItems = isSpeakers ? totalSpeakers : totalAttendees;
  const modalTotalPages = Math.ceil(totalModalItems / modalLimit);

  // Ref for modal search input to keep focus and cursor
  const modalInputRef = useRef(null);

  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedModalInput(modalInput.trim());
      setModalPage(1); // reset to first page on a new search
    }, 500);
    return () => clearTimeout(handler);
  }, [modalInput]);

  
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (modalInputRef.current) {
        modalInputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(focusTimer);
  }, [debouncedModalInput]);

  
  useEffect(() => {
    if (!id) return;
    if (isSpeakers) {
      dispatch(getAllSpeakersOfVisit(id));
    } else {
      dispatch(getAllAttendeesOfVisit(id));
    }
  }, [id, isSpeakers, dispatch]);

  
  useEffect(() => {
    if (!showModal) return;
    if (!selectedOfficeId) return;

    if (isSpeakers) {
      dispatch(
        getAllSpeakers({
          officeId: selectedOfficeId,
          page: modalPage,
          limit: modalLimit,
          search: debouncedModalInput,
        })
      );
    } else {
      dispatch(
        getAllAttendes({
          officeId: selectedOfficeId,
          page: modalPage,
          limit: modalLimit,
          search: debouncedModalInput,
        })
      );
    }
  }, [showModal, modalPage, debouncedModalInput, selectedOfficeId, isSpeakers, dispatch]);

 
  useEffect(() => {
    if (isSpeakers && visitSpeakerData.length) {
      const visitIds = visitSpeakerData.map((i) => i._id.toString());
      setPreSelectedIds((prev) => Array.from(new Set([...prev, ...visitIds])));
      setUpdatedSequence(visitSpeakerData);
    }
  }, [visitSpeakerData, isSpeakers]);

 
  useEffect(() => {
    if (!isSpeakers && visitAttendeeData.length) {
      const visitIds = visitAttendeeData.map((i) => i._id.toString());
      setPreSelectedIds((prev) => Array.from(new Set([...prev, ...visitIds])));
      // setUpdatedSequence(visitAttendeeData); // enable if attendees need reorder
    }
  }, [visitAttendeeData, isSpeakers]);

  
  const onSelectHandler = (rawId) => {
    const strId = rawId.toString();
    setPreSelectedIds((prev) =>
      prev.includes(strId)
        ? prev.filter((sid) => sid !== strId) // deselect
        : [...prev, strId]                     // select (append to existing)
    );
  };

  // Check if an id is currently selected (normalize to string)
  const checkIsSelected = (rawId) => preSelectedIds.includes(rawId.toString());

  
  function getSpeakersData() {
    if (!selectedOfficeId) return;
    dispatch(
      getAllSpeakers({ officeId: selectedOfficeId, page: 1, limit: 6, search: "" })
    );
  }
  function getVisitSpeakersData() {
    if (!id) return;
    dispatch(getAllSpeakersOfVisit(id));
  }
  function getAttendeesData() {
    if (!selectedOfficeId) return;
    dispatch(getAllAttendes({ officeId: selectedOfficeId, page: 1, limit: 6, search: "" }));
  }
  function getVisitAttendeesData() {
    if (!id) return;
    dispatch(getAllAttendeesOfVisit(id));
  }

  function handleCloseErrorAlert() {
    dispatch(resetErrorAlert(false));
  }

  useEffect(() => {
    if (isSpeakers) {
      getSpeakersData();
      getVisitSpeakersData();
    }
  }, [isSpeakers]);

  useEffect(() => {
    if (!isSpeakers) {
      getAttendeesData();
      getVisitAttendeesData();
    }
  }, [isSpeakers]);

  // Re-fetch visit data after a successful update
  useEffect(() => {
    if (isSpeakers) {
      getVisitSpeakersData();
    } else {
      getVisitAttendeesData();
    }
  }, [updatedVisitSpeakerData]);

  

  if (isLoading) {
    return <Loader />;
  }
  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  const onUpdateHandler = () => {
    if (showSorting) {
      let sequence = updatedSequence.map((item) => item._id);
      sequence.unshift("Sequence");
      dispatch(updateVisitSpeakers({ id: id, speakers: sequence }));
    }
    setSorting(!showSorting);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleListReorder = (result) => {
    if (!result.destination) return;
    const items = reorder(
      updatedSequence,
      result.source.index,
      result.destination.index
    );
    setUpdatedSequence(items);
  };

  
  const onAddHandler = () => {
    setShowModal(true);
    setModalPage(1);
    setModalInput("");
  };

  
  const onSubmitHandler = () => {
    setShowModal(false);
    setModalInput("");            
    setDebouncedModalInput(""); 
    if (isSpeakers) {
      dispatch(updateVisitSpeakers({ id: id, speakers: preSelectedIds }));
    } else {
      dispatch(updateVisitAttendees({ id: id, attendees: preSelectedIds }));
    }
  };

  return (
    <div className="speakers-list-container">
      <div className="speakers-list-heading">

        {isSpeakers
          ? TitansOfIndustryStringLabels.titansOfIndustry
          : TitansOfIndustryStringLabels.attendees}

        <div className="speaker-add-button">
          {isSpeakers && visitSpeakerData?.length !== 0 && (
            <BlackButton onClickhandle={onUpdateHandler} style={{ width: "80px" }}>
              {!showSorting ? "Update Order" : "Submit"}
            </BlackButton>
          )}
          {!showSorting && (
            <BlackButton onClickhandle={onAddHandler} style={{ width: "80px" }}>
              Add
            </BlackButton>
          )}
        </div>

        {!showSorting && (
          <div className="speakers-list-body-modal">
            {isSpeakers &&
              visitSpeakerData &&
              visitSpeakerData
                .filter((dataItem) =>
                  modalInput.toLowerCase() === ""
                    ? dataItem
                    : dataItem.name.toLowerCase().includes(modalInput)
                )
                .map((item) => (
                  <SpeakerCard
                    isSpeaker={true}
                    data={item}
                    key={item._id}
                    readOnly={true}
                  />
                ))}
            {!isSpeakers &&
              visitAttendeeData &&
              visitAttendeeData
                .filter((dataItem) =>
                  modalInput.toLowerCase() === ""
                    ? dataItem
                    : dataItem.name.toLowerCase().includes(modalInput)
                )
                .map((item) => (
                  <SpeakerCard
                    isSpeaker={false}
                    data={item}
                    key={item._id}
                    readOnly={true}
                  />
                ))}
          </div>
        )}

        {isSpeakers && showSorting && updatedSequence.length > 0 && (
          <div className="draggable-list">
            <DraggableList onDragEnd={handleListReorder} dataItems={updatedSequence} />
          </div>
        )}

        {isSpeakers && visitSpeakerData.length === 0 && (
          <img src={noTitanOfIndustry} alt="no titan of industry" className="no-img" />
        )}
        {!isSpeakers && visitAttendeeData.length === 0 && (
          <img src={noTitanOfIndustry} alt="no attendees" className="no-img" />
        )}
      </div>

      {showModal && (
        <div className="speaker-modal">
          <div className="speaker-form-scroll">
            <div className="speaker-form-flex">
              <div className="speaker-modal-content">
                <div style={{ justifySelf: "flex-end" }}>
                  <CloseButton
                    style={{ justifyContent: "flex-end" }}
                    onClickhandle={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-speaker-searchContainer">
                  <div className="speaker-searchIconText">
                    <FontAwesomeIcon size="2xs" icon={faMagnifyingGlass} color="#D1D1D1" />
                    <input
                      ref={modalInputRef}
                      className="search"
                      placeholder="Search"
                      value={modalInput}
                      onChange={(e) => {
                        setModalInput(e.target.value);
                        setModalPage(1);
                      }}
                      aria-label="Search speakers or attendees in modal"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="speaker-submit-button">
                  <BlackButton onClickhandle={onSubmitHandler}>Submit</BlackButton>
                </div>

                <div className="speakers-list-body-modal">
                  {modalData &&
                    modalData.length > 0 &&
                    modalData.map((item) => (
                      <VisitSpeakerCard
                        isSpeakers={isSpeakers}
                        data={item}
                        key={item._id}
                        onSelectHandler={() => onSelectHandler(item._id)}
                        isSelected={checkIsSelected(item._id)}
                      />
                    ))}
                  {modalData && modalData.length === 0 && (
                    <p style={{ textAlign: "center" }}>No records found</p>
                  )}
                </div>

                {modalTotalPages > 1 && (
                  <div className="floating-pagination">
                    <Stack spacing={2} sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                      <Pagination
                        count={modalTotalPages}
                        page={modalPage}
                        onChange={(_, value) => setModalPage(value)}
                        showFirstButton
                        showLastButton
                        color="primary"
                        aria-label="pagination"
                      />
                    </Stack>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitSpeakersList;